// src/HomePage/Game/FolkGame/FolkGameService.js
import { database } from '../../../firebaseConfig';
import {
  ref,
  push,
  set,
  get,
  update,
  remove,
  onValue,
  off,
  runTransaction
} from 'firebase/database';

// ⏱️ Thời gian phòng tự hủy nếu không có hoạt động: 10 phút (ms)
export const ROOM_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Kiểm tra xem phòng có bị quá hạn 10 phút không
 */
export const isRoomExpired = (room) => {
  if (!room) return true;
  const lastActive = room.lastActivityAt || room.createdAt || 0;
  return Date.now() - lastActive > ROOM_TIMEOUT_MS;
};

/**
 * Xóa trực tiếp một phòng (dùng cho cleanup và hủy bàn)
 */
export const deleteFolkRoom = async (gameType, roomId) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    await remove(roomRef);
    return { success: true };
  } catch (error) {
    console.error('Lỗi khi xóa phòng:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Cập nhật nhịp tim hoạt động của phòng
 */
export const touchRoomActivity = async (gameType, roomId) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    await update(roomRef, { lastActivityAt: Date.now() });
  } catch (e) {
    // Không block UI nếu touch lỗi
  }
};

/**
 * Lắng nghe danh sách phòng tại Sảnh
 * - Tự động LỌC BỎ các phòng quá 10 phút không hoạt động
 * - Kích hoạt LAZY CLEANUP: Xóa ngầm các phòng rác/phòng ma khỏi database
 */
export const subscribeFolkRooms = (gameType, callback) => {
  if (!gameType) return () => {};
  const roomsRef = ref(database, `folk_games/${gameType}/rooms`);

  const handleValue = (snapshot) => {
    const data = snapshot.val() || {};
    const now = Date.now();
    const activeRooms = [];
    const expiredRoomIds = [];

    Object.entries(data).forEach(([id, val]) => {
      const room = { id, ...val };
      const lastActive = room.lastActivityAt || room.createdAt || 0;

      if (now - lastActive > ROOM_TIMEOUT_MS) {
        // Gom các phòng quá hạn để xóa ngầm (Lazy Cleanup)
        expiredRoomIds.push(id);
      } else {
        activeRooms.push(room);
      }
    });

    // Sắp xếp phòng mới nhất lên đầu
    activeRooms.sort((a, b) => (b.lastActivityAt || b.createdAt || 0) - (a.lastActivityAt || a.createdAt || 0));
    callback(activeRooms);

    // Dọn dẹp ngầm các phòng hết hạn mà không làm chậm render của người dùng
    if (expiredRoomIds.length > 0) {
      expiredRoomIds.forEach((expId) => {
        const expRef = ref(database, `folk_games/${gameType}/rooms/${expId}`);
        remove(expRef).catch(() => {});
      });
    }
  };

  onValue(roomsRef, handleValue);
  return () => off(roomsRef, 'value', handleValue);
};

export const subscribeRoomDetail = (gameType, roomId, callback) => {
  if (!gameType || !roomId) return () => {};
  const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);

  const handleValue = (snapshot) => {
    if (snapshot.exists()) {
      const roomData = { id: snapshot.key, ...snapshot.val() };
      // Nếu phòng đã hết hạn 10 phút thì xem như không còn tồn tại
      if (isRoomExpired(roomData)) {
        deleteFolkRoom(gameType, roomId);
        callback(null);
      } else {
        callback(roomData);
      }
    } else {
      callback(null);
    }
  };

  onValue(roomRef, handleValue);
  return () => off(roomRef, 'value', handleValue);
};

export const getRoomSnapshot = async (gameType, roomId) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snapshot = await get(roomRef);
    if (snapshot.exists()) {
      const roomData = { id: snapshot.key, ...snapshot.val() };
      if (isRoomExpired(roomData)) {
        await deleteFolkRoom(gameType, roomId);
        return null;
      }
      return roomData;
    }
    return null;
  } catch (error) {
    console.error('Lỗi kiểm tra phòng:', error);
    return null;
  }
};

export const createFolkRoom = async (gameType, roomData) => {
  try {
    const roomsRef = ref(database, `folk_games/${gameType}/rooms`);
    const newRoomRef = push(roomsRef);
    const roomId = newRoomRef.key;
    const now = Date.now();

    // Sanitize inputs
    const safeName = (roomData.name || `Bàn chơi #${roomId.slice(-4)}`).slice(0, 60);
    const safePassword = (roomData.password || '').slice(0, 50);
    const safeFullName = (roomData.player1.fullName || 'Người chơi').slice(0, 40);
    const safeHolyName = (roomData.player1.holyName || '').slice(0, 30);

    const payload = {
      id: roomId,
      name: safeName,
      gameType,
      status: 'waiting',
      hasPassword: Boolean(safePassword),
      password: safePassword,
      createdAt: now,
      lastActivityAt: now,
      createdBy: roomData.player1.id,
      score: { p1: 0, p2: 0, draw: 0 },
      startingPlayerId: roomData.player1.id,
      currentTurn: roomData.player1.id,
      lastMoveAt: now,
      player1: {
        id: roomData.player1.id,
        fullName: safeFullName,
        holyName: safeHolyName,
        isCatechist: Boolean(roomData.player1.isCatechist),
        avatar: roomData.player1.isCatechist ? '✝️' : '🎓',
        ready: false,
        rematch: false,
        symbol: 'X',
      },
      player2: null,
      gameState: null,
      winner: null,
      endReason: '',
      kickedPlayerId: null
    };

    await set(newRoomRef, payload);
    return { success: true, roomId, room: payload };
  } catch (error) {
    console.error('Lỗi khi tạo phòng chơi:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Vào phòng với TRANSACTION chống Race Condition
 * Ngăn chặn 2 người cùng vào slot player2 cùng 1 thời điểm
 */
export const joinFolkRoom = async (gameType, roomId, player2Info, inputPassword = '') => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    let errorMessage = '';
    let updatedRoomResult = null;

    const transactionResult = await runTransaction(roomRef, (currentData) => {
      if (!currentData) {
        errorMessage = 'Bàn chơi không tồn tại hoặc đã bị hủy!';
        return; // Abort transaction
      }

      // Kiểm tra hết hạn 10 phút
      const lastActive = currentData.lastActivityAt || currentData.createdAt || 0;
      if (Date.now() - lastActive > ROOM_TIMEOUT_MS) {
        errorMessage = 'Bàn chơi đã tự hủy do hơn 10 phút không hoạt động!';
        return; // Abort
      }

      // Nếu đã là người chơi trong phòng -> Cho vào lại
      if (currentData.player1?.id === player2Info.id || currentData.player2?.id === player2Info.id) {
        currentData.lastActivityAt = Date.now();
        updatedRoomResult = { id: roomId, ...currentData };
        return currentData;
      }

      // Nếu phòng đã có người khác làm player2
      if (currentData.player2 && currentData.player2.id !== player2Info.id) {
        errorMessage = 'Bàn chơi đã có đối thủ vào trước!';
        return; // Abort
      }

      // Kiểm tra mật khẩu
      if (currentData.hasPassword && currentData.password !== inputPassword) {
        errorMessage = 'Mật khẩu bàn chơi không chính xác!';
        return; // Abort
      }

      // Điền slot player2 an toàn
      const safeFullName = (player2Info.fullName || 'Người chơi').slice(0, 40);
      const safeHolyName = (player2Info.holyName || '').slice(0, 30);

      const newPlayer2 = {
        id: player2Info.id,
        fullName: safeFullName,
        holyName: safeHolyName,
        isCatechist: Boolean(player2Info.isCatechist),
        avatar: player2Info.isCatechist ? '✝️' : '🎓',
        ready: false,
        rematch: false,
        symbol: 'O',
      };

      currentData.player2 = newPlayer2;
      currentData.status = 'waiting';
      currentData.kickedPlayerId = null;
      currentData.lastActivityAt = Date.now();

      updatedRoomResult = { id: roomId, ...currentData };
      return currentData;
    });

    if (!transactionResult.committed) {
      // Nếu phòng hết hạn, chủ động xóa luôn khỏi Firebase
      if (errorMessage.includes('không hoạt động')) {
        await remove(roomRef);
      }
      return { success: false, message: errorMessage || 'Không thể tham gia bàn chơi.' };
    }

    return { success: true, room: updatedRoomResult };
  } catch (error) {
    console.error('Lỗi khi tham gia phòng:', error);
    return { success: false, message: error.message };
  }
};

export const kickPlayerFromRoom = async (gameType, roomId, hostId) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snap = await get(roomRef);
    if (!snap.exists()) return { success: false };
    const room = snap.val();

    if (room.player1?.id !== hostId) {
      return { success: false, message: 'Chỉ chủ phòng mới có quyền mời đối thủ ra ngoài!' };
    }

    await update(roomRef, {
      player2: null,
      'player1/ready': false,
      'player1/rematch': false,
      status: 'waiting',
      kickedPlayerId: room.player2?.id || null,
      lastActivityAt: Date.now()
    });

    setTimeout(async () => {
      try {
        const currentSnap = await get(roomRef);
        if (!currentSnap.exists()) return;
        const currentRoom = currentSnap.val();
        if (currentRoom.kickedPlayerId && !currentRoom.player2) {
          await update(roomRef, { kickedPlayerId: null });
        }
      } catch (e) {}
    }, 10000);

    return { success: true };
  } catch (error) {
    console.error('Lỗi khi kick đối thủ:', error);
    return { success: false, message: error.message };
  }
};

export const surrenderFolkGame = async (gameType, roomId, opponentId, reason) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snap = await get(roomRef);
    if (!snap.exists()) return { success: false };
    const room = snap.val();
    if (room.status !== 'playing') return { success: false };

    const currentScore = room.score || { p1: 0, p2: 0, draw: 0 };
    const updates = {
      status: 'finished',
      winner: opponentId,
      endReason: reason || 'Đối thủ đã xin đầu hàng!',
      lastActivityAt: Date.now()
    };

    if (opponentId === room.player1?.id) {
      updates['score/p1'] = (currentScore.p1 || 0) + 1;
    } else if (opponentId === room.player2?.id) {
      updates['score/p2'] = (currentScore.p2 || 0) + 1;
    }

    await update(roomRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Lỗi khi đầu hàng:', error);
    return { success: false, message: error.message };
  }
};

export const leaveFolkRoom = async (gameType, roomId, playerId) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snapshot = await get(roomRef);

    if (!snapshot.exists()) return { success: true };
    const room = snapshot.val();

    const isPlayer1 = room.player1?.id === playerId;
    const isPlayer2 = room.player2?.id === playerId;

    // Chủ phòng rời đi
    if (isPlayer1) {
      if (room.player2) {
        const promotedPlayer = {
          ...room.player2,
          symbol: 'X',
          ready: false,
          rematch: false
        };

        const updates = {
          name: `Bàn của ${promotedPlayer.holyName ? promotedPlayer.holyName + ' ' : ''}${promotedPlayer.fullName}`,
          createdBy: promotedPlayer.id,
          player1: promotedPlayer,
          player2: null,
          status: 'waiting',
          gameState: null,
          winner: null,
          endReason: null,
          startingPlayerId: promotedPlayer.id,
          currentTurn: promotedPlayer.id,
          kickedPlayerId: null,
          lastActivityAt: Date.now()
        };

        if (room.status === 'playing') {
          const currentScore = room.score || { p1: 0, p2: 0, draw: 0 };
          updates['score/p2'] = (currentScore.p2 || 0) + 1;
        }

        await update(roomRef, updates);
        return { success: true, promoted: true };
      } else {
        await remove(roomRef);
        return { success: true, deleted: true };
      }
    }

    // Người chơi 2 rời đi
    if (isPlayer2) {
      if (room.player1) {
        const updates = {
          player2: null,
          status: 'waiting',
          'player1/ready': false,
          'player1/rematch': false,
          gameState: null,
          winner: null,
          endReason: null,
          kickedPlayerId: null,
          lastActivityAt: Date.now()
        };

        if (room.status === 'playing') {
          const currentScore = room.score || { p1: 0, p2: 0, draw: 0 };
          updates['score/p1'] = (currentScore.p1 || 0) + 1;
        }

        await update(roomRef, updates);
        return { success: true };
      } else {
        await remove(roomRef);
        return { success: true, deleted: true };
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Lỗi khi rời phòng:', error);
    return { success: false, message: error.message };
  }
};

export const togglePlayerReady = async (gameType, roomId, isPlayer1, readyStatus) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snap = await get(roomRef);
    if (!snap.exists()) return { success: false };
    const room = snap.val();

    const p1Ready = isPlayer1 ? readyStatus : Boolean(room.player1?.ready);
    const p2Ready = !isPlayer1 ? readyStatus : Boolean(room.player2?.ready);

    const updates = {
      [isPlayer1 ? 'player1/ready' : 'player2/ready']: readyStatus,
      lastActivityAt: Date.now()
    };

    if (p1Ready && p2Ready && room.player1 && room.player2) {
      updates.status = 'playing';
      updates.startingPlayerId = room.player1.id;
      updates.currentTurn = room.player1.id;
      updates.winner = null;
      updates.endReason = null;
      updates['player1/ready'] = false;
      updates['player2/ready'] = false;
      updates['player1/rematch'] = false;
      updates['player2/rematch'] = false;
      updates.gameState = null;
      updates.lastMoveAt = Date.now();
    }

    await update(roomRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Lỗi cập nhật sẵn sàng:', error);
    return { success: false, message: error.message };
  }
};

export const toggleRematchReady = async (gameType, roomId, isPlayer1, rematchStatus) => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snap = await get(roomRef);
    if (!snap.exists()) return { success: false };
    const room = snap.val();

    if (room.status !== 'finished') {
      return { success: false, message: 'Ván đấu chưa kết thúc!' };
    }

    const p1Rematch = isPlayer1 ? rematchStatus : Boolean(room.player1?.rematch);
    const p2Rematch = !isPlayer1 ? rematchStatus : Boolean(room.player2?.rematch);

    const updates = {
      [isPlayer1 ? 'player1/rematch' : 'player2/rematch']: rematchStatus,
      lastActivityAt: Date.now()
    };

    if (p1Rematch && p2Rematch) {
      updates.status = 'playing';
      updates.winner = null;
      updates.endReason = null;
      updates['player1/rematch'] = false;
      updates['player2/rematch'] = false;
      updates['player1/ready'] = false;
      updates['player2/ready'] = false;

      const previousStarter = room.startingPlayerId || room.player1.id;
      const nextStarter = previousStarter === room.player1.id
        ? (room.player2?.id || room.player1.id)
        : room.player1.id;

      updates.startingPlayerId = nextStarter;
      updates.currentTurn = nextStarter;
      updates.gameState = null;
      updates.lastMoveAt = Date.now();
    }

    await update(roomRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Lỗi xử lý đấu lại:', error);
    return { success: false, message: error.message };
  }
};

export const quickJoinFolkRoom = async (gameType, playerInfo) => {
  try {
    const roomsRef = ref(database, `folk_games/${gameType}/rooms`);
    const snapshot = await get(roomsRef);
    const rooms = snapshot.val() || {};
    const now = Date.now();

    // Chỉ tìm phòng hợp lệ, còn hạn và chưa đủ người
    const candidateIds = Object.keys(rooms).filter((id) => {
      const r = rooms[id];
      const lastActive = r.lastActivityAt || r.createdAt || 0;
      const isExpired = now - lastActive > ROOM_TIMEOUT_MS;
      if (isExpired) {
        remove(ref(database, `folk_games/${gameType}/rooms/${id}`)).catch(() => {});
        return false;
      }
      return r.status === 'waiting' && !r.hasPassword && !r.player2 && r.player1?.id !== playerInfo.id;
    });

    if (candidateIds.length === 0) {
      return { success: false, message: 'Hiện không có bàn nào đang chờ. Hãy tự tạo bàn mới nhé!' };
    }

    // Chọn bàn đầu tiên và dùng joinFolkRoom (với Transaction) để đảm bảo không tranh chấp
    return await joinFolkRoom(gameType, candidateIds[0], playerInfo);
  } catch (error) {
    console.error('Lỗi ghép phòng nhanh:', error);
    return { success: false, message: error.message };
  }
};

export const updateFolkGameState = async (gameType, roomId, nextState, nextTurn, winner = null, endReason = '') => {
  try {
    const roomRef = ref(database, `folk_games/${gameType}/rooms/${roomId}`);
    const snap = await get(roomRef);
    const room = snap.val() || {};

    if (room.status === 'finished' && !winner) {
      return { success: false, message: 'Ván đấu đã kết thúc' };
    }

    const now = Date.now();
    const updates = {
      gameState: nextState,
      currentTurn: nextTurn,
      lastMoveAt: now,
      lastActivityAt: now
    };

    if (winner) {
      updates.status = 'finished';
      updates.winner = winner;
      if (endReason) updates.endReason = endReason;
      const currentScore = room.score || { p1: 0, p2: 0, draw: 0 };
      if (winner === 'DRAW') {
        updates['score/draw'] = (currentScore.draw || 0) + 1;
      } else if (winner === room.player1?.id) {
        updates['score/p1'] = (currentScore.p1 || 0) + 1;
      } else if (winner === room.player2?.id) {
        updates['score/p2'] = (currentScore.p2 || 0) + 1;
      }
    }

    await update(roomRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Lỗi cập nhật nước cờ:', error);
    return { success: false, message: error.message };
  }
};